// Where this count came from — the scrutineer's trail.
//
// A native <details>/<summary>, never an ARIA re-implementation: the disclosure
// state, the keyboard behaviour and the announcement all come free from the
// platform, and every hand-rolled version of this control is worse than the one
// the browser ships.
//
// It is CLOSED by default and it is the only control on this screen. The digests
// are here rather than beside the tallies because they are the answer to "prove
// this count is the one that was run", which is a question a scrutineer asks and
// nobody else — and because a page of hex above a tally teaches a reader to skim
// past the numbers.
//
// The engine's own issue messages are repeated here VERBATIM, each beside its
// IssueCode. That code is the ONE place on this screen it appears, and the
// reason it appears here and nowhere else is that this list is the raw one:
// `votes-exceed-roll` is the string you grep the engine for, and the message
// beside it names fields and says things like "(carried: null)". IssueList says
// the same findings in plain words and never shows a code. Neither list is a
// substitute for the other, and nothing here is gated on the reader — the whole
// PAGE is Admin and Scrutineers (registrar ruling 2026-08-16; see the banner at
// the top of ResultPage.tsx), so there is no second audience to divide it for.

import type { JSX } from 'react';
import type { CountResult } from '@cca/decides-count';
import type { ProvenanceView } from './view';

interface ProvenanceProps {
  provenance: ProvenanceView;
  result: CountResult;
}

export function Provenance({ provenance, result }: ProvenanceProps): JSX.Element {
  const messages = [...result.fatal, ...result.issues];

  // ONE definition of the fact list, rendered TWICE — inside the disclosure for
  // the screen, and again in a print-only block below it. The duplication is
  // deliberate and the lesser cost: a closed <details> prints none of its
  // content, page CSS cannot open one, and the `::details-content` override is
  // too new to bet a synod's printer on — while the printed sheet MUST carry the
  // digests and the run id (they are what a countersigning President is handed).
  // Because both placements render this same element, they cannot drift apart.
  const facts = (
    <dl className="result-provenance-list">
      <div className="result-provenance-row">
        <dt>Counted at</dt>
        <dd>{provenance.runAtNz}</dd>
      </div>
      <div className="result-provenance-row">
        <dt>Count run</dt>
        <dd className="result-mono">{provenance.countRunId}</dd>
      </div>
      <div className="result-provenance-row">
        <dt>Ballot</dt>
        <dd className="result-mono">{result.ballotId}</dd>
      </div>
      <div className="result-provenance-row">
        <dt>Mode</dt>
        <dd>{result.mode}</dd>
      </div>
      <div className="result-provenance-row">
        <dt>Counting engine</dt>
        <dd>version {provenance.engineVersion}</dd>
      </div>
      <div className="result-provenance-row">
        <dt>Definition digest</dt>
        <dd className="result-mono">{provenance.definitionDigest}</dd>
      </div>
      <div className="result-provenance-row">
        <dt>Result digest</dt>
        <dd className="result-mono">{provenance.resultDigest}</dd>
      </div>
      {/* The roll — the third digest of the trail, because "against this roll"
          is half the sentence a signature is. Null is NO FROZEN ROLL (a
          rehearsal before the freeze) and draws its own plain words, never an
          empty mono cell that reads as a digest gone missing. */}
      <div className="result-provenance-row">
        <dt>Roll digest</dt>
        {provenance.rollDigest !== null ? (
          <dd className="result-mono">{provenance.rollDigest}</dd>
        ) : (
          <dd>no frozen roll for this sitting</dd>
        )}
      </div>
      {provenance.rollAmendmentSeq !== null ? (
        <div className="result-provenance-row">
          <dt>Roll amendments applied</dt>
          <dd>{provenance.rollAmendmentSeq}</dd>
        </div>
      ) : null}
    </dl>
  );

  return (
    <>
      <details className="result-provenance">
        <summary className="result-provenance-summary">How this count was produced</summary>

        {facts}

        <p className="result-provenance-note">
          The counting engine is pure and integer-only, and it ships inside the scrutineer&rsquo;s
          export: the same papers and the same roll produce the same bytes, on any machine, so this
          count can be re-run and compared rather than taken on trust.
        </p>

        {messages.length > 0 ? (
          <>
            <p className="result-provenance-note">What the engine reported, in its own words:</p>
            <ul className="result-provenance-messages">
              {messages.map((issue, index) => (
                <li key={`${issue.code}-${String(index)}`}>
                  <span className="result-mono">{issue.code}</span> ({issue.severity}) —{' '}
                  {issue.message}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </details>

      {/* The print copy: the facts and nothing else. The reproducibility note is
          screen prose, and the engine's raw messages are the grep list — the
          plain-words IssueList higher up already prints every finding. Hidden on
          screen (result.css), shown by print.css. */}
      <section className="result-provenance-print">
        <h2 className="result-subheading">How this count was produced</h2>
        {facts}
      </section>
    </>
  );
}
